'use client';

import { Button, ButtonProps } from '@chakra-ui/react';
import { Broker, NestedUser, SellerIntentType } from '@habity/types';
import { buildCalDataAttributes } from '~/hooks/providers/useCal';

interface CalButtonProps extends Omit<ButtonProps, 'onClick'> {
  broker: Broker;
  intent?: SellerIntentType;
  user?: NestedUser | null;
  children: React.ReactNode;
}

/**
 * Button component that opens Cal.com booking modal on click
 * Uses data-cal attributes for declarative integration
 * Cal.com is initialized globally in App provider
 *
 * @example
 * <CalButton broker={broker} intent="meeting" user={user}>
 *   Boka möte
 * </CalButton>
 */
export function CalButton({
  broker,
  intent,
  user,
  children,
  ...buttonProps
}: CalButtonProps) {
  if (!broker.cal_username) {
    return null;
  }

  // Use broker's preferred meeting type for "meeting" intent, otherwise use the provided intent
  const effectiveEventType =
    intent === 'meeting' ? broker.cal_meeting_type || 'meeting' : intent;

  const calDataAttributes = buildCalDataAttributes(broker.cal_username, {
    user,
    eventType: effectiveEventType,
    customData: {
      intent: intent || 'meeting',
      broker_email: broker.email || '',
      broker_name: broker.name || '',
    },
  });

  return (
    <Button {...buttonProps} {...calDataAttributes}>
      {children}
    </Button>
  );
}
